/**
 * Zoom and pan maths of the collage board ("Raspored slika"), pure.
 *
 * The board shows the print side at "fit" (the whole sheet inside the stage)
 * and lets the shopper zoom from 1× (fit) to 4× on a phone (3× on a desktop).
 * A view is where the sheet's top-left corner sits on the stage, in CSS px,
 * and how much it is zoomed on top of the fit:
 *
 *   screen = view.x + scene · fit · zoom      (same for y)
 *
 * Every function returns a new view and never touches the DOM, so the
 * gesture code, the zoom buttons, "Cela strana" and "‹ ›" all agree, and the
 * maths is tested without a browser.
 */

export interface Size {
  w: number;
  h: number;
}

export interface Point {
  x: number;
  y: number;
}

/** A rectangle; in scene px for `visibleSceneRect`, in stage px elsewhere. */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface BoardView {
  /** Stage CSS px per scene px at zoom 1: the whole sheet fits the stage. */
  fit: number;
  /** 1 = fit. */
  zoom: number;
  /** Where the sheet's top-left corner sits on the stage, in CSS px. */
  x: number;
  y: number;
}

export interface ZoomLimits {
  min: number;
  max: number;
}

/** Phones zoom further than desktops: a thumb needs a bigger target on a 6-inch screen. */
export const PHONE_ZOOM: ZoomLimits = { min: 1, max: 4 };
export const DESKTOP_ZOOM: ZoomLimits = { min: 1, max: 3 };
/** How far the sheet may be dragged past its edge while a finger is down; it springs back on release. */
export const OVERSCROLL = 40;
/** The double tap's zoom, about the tapped point. */
export const DOUBLE_TAP_ZOOM = 2.5;

export function zoomLimitsFor(pointer: 'touch' | 'mouse'): ZoomLimits {
  return pointer === 'mouse' ? DESKTOP_ZOOM : PHONE_ZOOM;
}

/** Stage CSS px per scene px. */
export function viewScale(view: BoardView): number {
  return view.fit * view.zoom;
}

/** The whole sheet, centred, at zoom 1, `pad` CSS px clear of the stage's edges. */
export function fitView(stage: Size, scene: Size, pad = 0): BoardView {
  const room = { w: Math.max(1, stage.w - 2 * pad), h: Math.max(1, stage.h - 2 * pad) };
  const fit = scene.w > 0 && scene.h > 0 ? Math.min(room.w / scene.w, room.h / scene.h) : 1;
  const safe = Number.isFinite(fit) && fit > 0 ? fit : 1;
  return { fit: safe, zoom: 1, x: (stage.w - scene.w * safe) / 2, y: (stage.h - scene.h * safe) / 2 };
}

/** A stage point in scene px. */
export function toScene(view: BoardView, point: Point): Point {
  const s = viewScale(view);
  return { x: (point.x - view.x) / s, y: (point.y - view.y) / s };
}

/** A scene point on the stage, in CSS px. */
export function toStage(view: BoardView, point: Point): Point {
  const s = viewScale(view);
  return { x: view.x + point.x * s, y: view.y + point.y * s };
}

export function clampZoom(zoom: number, limits: ZoomLimits): number {
  if (!Number.isFinite(zoom)) return limits.min;
  return Math.min(limits.max, Math.max(limits.min, zoom));
}

/**
 * Zooms by `factor` about a stage point, keeping the part of the sheet under
 * that point where it is (the pinch midpoint, the mouse pointer, the double
 * tap). The zoom stays within `limits`.
 */
export function zoomAt(view: BoardView, factor: number, point: Point, limits: ZoomLimits): BoardView {
  const zoom = clampZoom(view.zoom * factor, limits);
  const k = zoom / view.zoom;
  return {
    fit: view.fit,
    zoom,
    x: point.x - (point.x - view.x) * k,
    y: point.y - (point.y - view.y) * k,
  };
}

/** Moves the sheet by a drag's distance, in stage px. */
export function panBy(view: BoardView, dx: number, dy: number): BoardView {
  return { ...view, x: view.x + dx, y: view.y + dy };
}

/**
 * Keeps the sheet on the stage. Along an axis where the sheet is smaller than
 * the stage it stays centred; where it is bigger, no edge comes further in
 * than the stage's edge. `overscroll` px of play either way (a finger dragging
 * past the edge feels the rubber band; 0 on release).
 */
export function clampView(view: BoardView, stage: Size, scene: Size, overscroll = 0): BoardView {
  const s = viewScale(view);
  const axis = (pos: number, content: number, room: number): number => {
    if (content <= room) {
      const centre = (room - content) / 2;
      return Math.min(centre + overscroll, Math.max(centre - overscroll, pos));
    }
    return Math.min(overscroll, Math.max(room - content - overscroll, pos));
  };
  return { ...view, x: axis(view.x, scene.w * s, stage.w), y: axis(view.y, scene.h * s, stage.h) };
}

/**
 * The part of the sheet on the stage, in whole scene px (rounded outwards,
 * inside the sheet): what the detail render draws. Empty (w or h 0) when the
 * sheet is off the stage altogether.
 */
export function visibleSceneRect(view: BoardView, stage: Size, scene: Size): Rect {
  const s = viewScale(view);
  const x0 = Math.max(0, Math.floor((0 - view.x) / s));
  const y0 = Math.max(0, Math.floor((0 - view.y) / s));
  const x1 = Math.min(scene.w, Math.ceil((stage.w - view.x) / s));
  const y1 = Math.min(scene.h, Math.ceil((stage.h - view.y) / s));
  return { x: x0, y: y0, w: Math.max(0, x1 - x0), h: Math.max(0, y1 - y0) };
}

/**
 * The view that shows one slot in the middle of the stage at `zoom` — never
 * so far in that the slot itself leaves the stage (90 % of it at most), and
 * never past the sheet's edge. For "‹ ›" and for opening the board on a slot.
 * `slot` is the slot's centre and size in scene px.
 */
export function focusOn(
  view: BoardView,
  slot: { cx: number; cy: number; w: number; h: number },
  zoom: number,
  stage: Size,
  scene: Size,
  limits: ZoomLimits,
): BoardView {
  const fitsSlot = Math.min(
    (0.9 * stage.w) / Math.max(1e-6, slot.w * view.fit),
    (0.9 * stage.h) / Math.max(1e-6, slot.h * view.fit),
  );
  const z = clampZoom(Math.min(zoom, fitsSlot), limits);
  const s = view.fit * z;
  const next = { fit: view.fit, zoom: z, x: stage.w / 2 - slot.cx * s, y: stage.h / 2 - slot.cy * s };
  return clampView(next, stage, scene, 0);
}

/**
 * The same part of the sheet on a stage of another size (a phone turned, a
 * window resized): the scene point that was in the middle stays in the
 * middle, at the same zoom over the new fit.
 */
export function refitView(view: BoardView, oldStage: Size, stage: Size, scene: Size, pad = 0): BoardView {
  const centre = toScene(view, { x: oldStage.w / 2, y: oldStage.h / 2 });
  const fitted = fitView(stage, scene, pad);
  const s = fitted.fit * view.zoom;
  const next = { fit: fitted.fit, zoom: view.zoom, x: stage.w / 2 - centre.x * s, y: stage.h / 2 - centre.y * s };
  return clampView(next, stage, scene, 0);
}

/** Two views that put the sheet in the same place (to skip a redundant render). */
export function sameView(a: BoardView, b: BoardView, tolerance = 0.5): boolean {
  return (
    Math.abs(a.x - b.x) <= tolerance &&
    Math.abs(a.y - b.y) <= tolerance &&
    Math.abs(a.zoom - b.zoom) <= 1e-4 &&
    Math.abs(a.fit - b.fit) <= 1e-9
  );
}

/**
 * Where a slot is under a stage point: the slot's box turned by its rotation
 * about its centre, as the renderer draws it. `slots` in scene px.
 */
export function slotAt<T extends { cx: number; cy: number; w: number; h: number; rotation?: number }>(
  view: BoardView,
  point: Point,
  slots: readonly T[],
): T | undefined {
  const p = toScene(view, point);
  // Last drawn on top wins, the way the hit layer's buttons stack.
  for (let i = slots.length - 1; i >= 0; i--) {
    const slot = slots[i]!;
    const r = ((slot.rotation ?? 0) * Math.PI) / 180;
    const dx = p.x - slot.cx;
    const dy = p.y - slot.cy;
    const lx = dx * Math.cos(-r) - dy * Math.sin(-r);
    const ly = dx * Math.sin(-r) + dy * Math.cos(-r);
    if (Math.abs(lx) <= slot.w / 2 && Math.abs(ly) <= slot.h / 2) return slot;
  }
  return undefined;
}

/**
 * The scale the whole sheet is rendered at: the stage's density (device
 * pixels, at most 2 per CSS px) with the long side at most `maxSide` device px.
 */
export function sheetRenderScale(view: BoardView, scene: Size, dpr: number, maxSide = 2048): number {
  const density = view.fit * Math.min(Math.max(dpr || 1, 1), 2);
  const cap = maxSide / Math.max(1, scene.w, scene.h);
  return Math.max(1e-4, Math.min(density, cap));
}

/**
 * The detail render of the visible part: the viewport at the stage's density
 * (fit · zoom · min(dpr, 2)), with the long side at most `maxSide` device px.
 */
export function detailRenderScale(view: BoardView, rect: Rect, dpr: number, maxSide = 2048): number {
  const density = viewScale(view) * Math.min(Math.max(dpr || 1, 1), 2);
  const cap = maxSide / Math.max(1, rect.w, rect.h);
  return Math.max(1e-4, Math.min(density, cap));
}
